import React, { useState, memo, useEffect } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Loader as LoaderIcon, Link2 as Link2Icon } from 'react-feather'
import { isEmpty } from 'lodash'
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
} from '@material-ui/core'

import { LoadingFeedback } from 'components'

import { DetailsReadingCard } from './components'
import { BannerHeadCard } from '../'

import useSnackbar from 'hooks/useSnackbar'
import useCookieBanner from 'hooks/useCookieBanner'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import styles from './styles'
import theme from 'theme'

const CookieScannerCard = () => {
  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()
  const { watch, control, setValue } = useFormContext()

  const {
    banner,
    refreshBanner,
    nextStep,
    setStepSubmit,
    isCompiling,
    isScanning,
  } = useCookieBanner()

  const watchedUrls = watch('urls')
  const watchedUrlId = watch('scannerUrlId')
  const loadingScanner = loading || isScanning
  const useStyles = styles(loadingScanner)
  const classes = useStyles()

  // Atualiza o valor do scanner quando URLs carregam
  useEffect(() => {
    const firstUrlId = watchedUrls?.[0]?.id
    if (
      !isEmpty(watchedUrls) &&
      isEmpty(watchedUrlId) &&
      firstUrlId !== undefined
    ) {
      setValue('scannerUrlId', firstUrlId)
    }
  }, [watchedUrls, watchedUrlId, setValue])

  const handleScan = async () => {
    try {
      setLoading(true)
      await service.cookies.banner.scanner({
        bannerId: banner?.id,
        urlId: watchedUrlId,
      })
      snackbar.open({
        message:
          'Escaneamento iniciado com sucesso! Isso pode levar alguns segundos.',
        variant: 'success',
      })
      refreshBanner()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  // Registra a função de submit no provider quando o componente é montado
  useEffect(() => {
    setStepSubmit(nextStep)
  }, [setStepSubmit])

  return (
    <Paper
      variant="outlined"
      id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_7}
    >
      <LoadingFeedback open={loading} />
      <BannerHeadCard name="Escaneamento de Cookies" />
      <Divider />
      <Box
        p={2}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(2)}
      >
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
          <Box>
            <Typography color="primary" variant="h6" gutterBottom>
              Detalhes do Scanner
            </Typography>
            <DetailsReadingCard scanner={banner?.scanner} />
          </Box>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            <Grid item xs={12} md={4} lg={3}>
              <Controller
                name="scannerUrlId"
                control={control}
                defaultValue=""
                as={
                  <TextField
                    select
                    size="small"
                    label="Selecione a URL"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Link2Icon size={20} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {watchedUrls?.map((url) => (
                      <MenuItem key={url.id} value={url.id}>
                        {url.url}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <Tooltip
                title={
                  loadingScanner
                    ? 'Por favor, atualize a página para verificar se o escaneamento foi concluído.'
                    : isCompiling
                    ? 'Por favor, atualize a página para verificar se a geração do código foi concluída.'
                    : ''
                }
              >
                <Box>
                  <Button
                    onClick={handleScan}
                    variant="contained"
                    color="primary"
                    startIcon={
                      <LoaderIcon size={18} className={classes.iconAnimation} />
                    }
                    disabled={loadingScanner || isCompiling}
                    fullWidth
                  >
                    {loadingScanner ? 'Escaneando' : 'Escanear'}
                  </Button>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  )
}

export default memo(CookieScannerCard)
